import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { FiArrowLeft, FiEdit2, FiShare2, FiClock, FiAlertCircle, FiCheckCircle, FiXCircle } from 'react-icons/fi'
import { successStoriesApi } from '@/services/api/successStories'
import { SuccessStory } from '@/types/successStory'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import ShareModal from '@/components/ui/ShareModal'
import { useToast } from '@/hooks/useToast'
import { extractErrorMessage } from '@/utils/errorHandler'

const MySuccessStoryPage: React.FC = () => {
    const navigate = useNavigate()
    const { addToast } = useToast()

    const [story, setStory] = useState<SuccessStory | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [shareModalOpen, setShareModalOpen] = useState(false)

    useEffect(() => {
        const fetchStory = async () => {
            try {
                setLoading(true)
                setError(null)
                const response = await successStoriesApi.getUserSuccessStory()

                if (response.data?.story) {
                    setStory(response.data.story)
                }
            } catch (err: any) {
                const message = extractErrorMessage(err, 'Failed to load your success story')
                setError(message)
                console.error('Error fetching story:', err)
            } finally {
                setLoading(false)
            }
        }

        fetchStory()
    }, [])

    const handleEdit = () => {
        if (story && story.status !== 'archived') {
            navigate(`/success-stories/${story._id}/edit`)
        } else {
            addToast('You cannot edit archived stories', 'info')
        }
    }

    const handleDelete = async () => {
        if (!story || story.status !== 'pending') {
            addToast('You can only delete pending stories', 'info')
            return
        }

        if (!window.confirm('Are you sure you want to delete this story? This action cannot be undone.')) {
            return
        }

        try {
            await successStoriesApi.deleteSuccessStory(story._id)
            addToast('Success story deleted', 'success')
            setStory(null)
        } catch (err: any) {
            const message = extractErrorMessage(err, 'Failed to delete story')
            addToast(message, 'error')
        }
    }

    const handleShare = () => {
        if (story?.status === 'approved') {
            setShareModalOpen(true)
        } else {
            addToast('Only approved stories can be shared', 'info')
        }
    }

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'pending':
                return (
                    <div className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1 bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 text-xs sm:text-ui font-medium rounded-full whitespace-nowrap">
                        <FiClock className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                        <span className="hidden sm:inline">Pending Review</span>
                        <span className="sm:hidden">Pending</span>
                    </div>
                )
            case 'approved':
                return (
                    <div className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1 bg-success-600 dark:bg-success-700 text-gray-50 text-xs sm:text-ui font-medium rounded-full whitespace-nowrap">
                        <FiCheckCircle className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                        Published
                    </div>
                )
            case 'rejected':
                return (
                    <div className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1 bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 text-xs sm:text-ui font-medium rounded-full whitespace-nowrap">
                        <FiXCircle className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                        Rejected
                    </div>
                )
            default:
                return null
        }
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <LoadingSpinner />
            </div>
        )
    }

    if (error || !story) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 md:py-12">
                    <button
                        onClick={() => navigate('/dashboard')}
                        className="flex items-center text-primary-600 hover:text-primary-700 mb-6 sm:mb-8 font-medium text-sm sm:text-base"
                    >
                        <FiArrowLeft className="w-4 h-4 mr-2" />
                        Back to Dashboard
                    </button>

                    <div className="bg-white dark:bg-gray-800 rounded-lg sm:rounded-xl p-6 sm:p-8 text-center">
                        <FiAlertCircle className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 dark:text-gray-600 mx-auto mb-3 sm:mb-4" />
                        <h2 className="text-lg sm:heading-card text-gray-900 dark:text-white mb-2">No Success Story Yet</h2>
                        <p className="text-xs sm:text-body text-gray-600 dark:text-gray-400 mb-4 sm:mb-6">
                            {error || 'You haven\'t submitted a success story yet. Share your journey with our community!'}
                        </p>
                        <button
                            onClick={() => navigate('/success-stories/submit')}
                            className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium text-sm sm:text-base rounded-lg transition-colors"
                        >
                            Submit Your Story
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 md:py-12">
                {/* Header */}
                <div className="mb-6 sm:mb-8">
                    <button
                        onClick={() => navigate('/dashboard')}
                        className="flex items-center text-primary-600 hover:text-primary-700 mb-3 sm:mb-4 font-medium text-sm sm:text-base"
                    >
                        <FiArrowLeft className="w-4 h-4 mr-2" />
                        Back to Dashboard
                    </button>
                    <div className="flex flex-row justify-between items-start gap-2 sm:gap-0">
                        <div className="flex-1">
                            <h1 className="text-2xl sm:text-3xl md:heading-1 text-gray-900 dark:text-white mb-1 sm:mb-2">My Success Story</h1>
                            <p className="text-xs sm:text-body text-gray-600 dark:text-gray-400">
                                View and manage your success story
                            </p>
                        </div>
                        <div className="flex-shrink-0">
                            {getStatusBadge(story.status)}
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <article className="bg-white dark:bg-gray-800 rounded-lg sm:rounded-xl p-4 sm:p-6 md:p-8 lg:p-12 shadow-md mb-6 sm:mb-8">
                    {/* Status Alerts */}
                    {story.status === 'pending' && (
                        <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-yellow-50 dark:bg-yellow-900 border-l-4 border-yellow-400 dark:border-yellow-600 rounded">
                            <p className="text-xs sm:text-ui text-yellow-800 dark:text-yellow-200 leading-relaxed">
                                ✏️ Your story is pending review. Our team will review it shortly and notify you when it's published.
                            </p>
                        </div>
                    )}

                    {story.status === 'rejected' && (
                        <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-50 dark:bg-red-900 border-l-4 border-red-400 dark:border-red-600 rounded">
                            <p className="text-xs sm:text-ui text-red-800 dark:text-red-200 mb-1 sm:mb-2">
                                ❌ Your story was not approved.
                            </p>
                            {story.rejectionReason && (
                                <p className="text-xs sm:text-ui text-red-800 dark:text-red-200">
                                    <strong>Reason:</strong> {story.rejectionReason}
                                </p>
                            )}
                            <p className="text-xs sm:text-ui text-red-800 dark:text-red-200 mt-1 sm:mt-2">
                                You can edit and resubmit your story.
                            </p>
                        </div>
                    )}

                    {story.status === 'approved' && story.featured && (
                        <div className="mb-4 sm:mb-6 p-4 sm:p-5 bg-success-600 dark:bg-success-700 border-l-4 border-success-400 dark:border-success-600 rounded">
                            <p className="text-base sm:text-lg text-gray-50 font-semibold leading-relaxed">
                                ⭐ Congratulations! Your story has been featured and is now visible to all users.
                            </p>
                        </div>
                    )}

                    {story.status === 'approved' && !story.featured && (
                        <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-success-50 dark:bg-success-900 border-l-4 border-success-400 dark:border-success-600 rounded">
                            <p className="text-xs sm:text-ui text-success-800 dark:text-success-200">
                                ✅ Your story has been published and is now visible to all users.
                            </p>
                        </div>
                    )}

                    {/* Title */}
                    <div className="mb-6 sm:mb-8">
                        <h2 className="text-xl sm:text-2xl md:heading-2 text-gray-900 dark:text-white mb-2">{story.title}</h2>
                        <div className="flex flex-wrap gap-2 sm:gap-3 text-xs sm:text-ui text-gray-600 dark:text-gray-400">
                            <span className="inline-block px-2 sm:px-3 py-0.5 sm:py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full capitalize">
                                {story.category}
                            </span>
                            <span className="py-0.5 sm:py-1">Submitted {new Date(story.submittedAt).toLocaleDateString()}</span>
                        </div>
                    </div>

                    {/* Achievement */}
                    <div className="mb-6 sm:mb-8 p-4 sm:p-5 md:p-6 bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900 dark:to-primary-800 border-2 border-primary-200 dark:border-primary-700 rounded-lg">
                        <h3 className="text-base sm:text-lg md:text-xl font-bold text-primary-900 dark:text-primary-100 leading-relaxed break-words">
                            🏆 {story.achievement}
                        </h3>
                    </div>

                    {/* Story */}
                    <div className="mb-6 sm:mb-8">
                        <h3 className="text-sm sm:text-lg md:font-heading font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">Your Journey</h3>
                        <p className="text-xs sm:text-body text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
                            {story.story}
                        </p>
                    </div>

                    {/* Testimonial */}
                    <div className="mb-6 sm:mb-8 p-3 sm:p-4 md:p-6 border-l-4 border-primary-600 bg-gray-50 dark:bg-gray-700 rounded">
                        <p className="text-xs sm:text-body italic text-gray-700 dark:text-gray-300">
                            "{story.testimonial}"
                        </p>
                    </div>

                    {/* Skills */}
                    <div className="mb-6 sm:mb-8">
                        <h3 className="text-sm sm:text-lg md:font-heading font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">Skills Highlighted</h3>
                        <div className="flex flex-wrap gap-1.5 sm:gap-2 md:gap-3">
                            {story.skills.map((skill, index) => (
                                <span
                                    key={index}
                                    className="px-2.5 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs sm:text-ui font-medium rounded-full"
                                >
                                    {skill}
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* Stats */}
                    {story.status === 'approved' && (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3 md:gap-4 p-3 sm:p-4 md:p-6 bg-gray-50 dark:bg-gray-700 rounded-lg">
                            <div>
                                <div className="text-xs sm:text-ui text-gray-600 dark:text-gray-400 mb-0.5 sm:mb-1">Views</div>
                                <div className="text-lg sm:text-2xl md:font-heading font-bold text-gray-900 dark:text-white">
                                    {story.views}
                                </div>
                            </div>
                            <div>
                                <div className="text-xs sm:text-ui text-gray-600 dark:text-gray-400 mb-0.5 sm:mb-1">Helpful</div>
                                <div className="text-lg sm:text-2xl md:font-heading font-bold text-gray-900 dark:text-white">
                                    {story.helpful}
                                </div>
                            </div>
                            <div>
                                <div className="text-xs sm:text-ui text-gray-600 dark:text-gray-400 mb-0.5 sm:mb-1">Published</div>
                                <div className="text-lg sm:text-2xl md:font-heading font-bold text-success-600 dark:text-success-400">
                                    ✓
                                </div>
                            </div>
                        </div>
                    )}
                </article>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 md:gap-4">
                    {/* Edit Button - Available for all non-archived stories */}
                    <button
                        onClick={handleEdit}
                        className="flex-1 flex items-center justify-center gap-2 px-4 sm:px-6 py-2 sm:py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium text-sm sm:text-base rounded-lg transition-colors"
                    >
                        <FiEdit2 className="w-4 h-4" />
                        Edit Story
                    </button>

                    {/* Delete Button - Only for pending stories */}
                    {story.status === 'pending' && (
                        <button
                            onClick={handleDelete}
                            className="flex-1 px-4 sm:px-6 py-2 sm:py-3 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900 font-medium text-sm sm:text-base rounded-lg transition-colors"
                        >
                            Delete
                        </button>
                    )}

                    {/* Share Button - Only for approved stories */}
                    {story.status === 'approved' && (
                        <button
                            onClick={handleShare}
                            className="flex-1 flex items-center justify-center gap-2 px-4 sm:px-6 py-2 sm:py-3 bg-success-600 hover:bg-success-700 text-white font-medium text-sm sm:text-base rounded-lg transition-colors"
                        >
                            <FiShare2 className="w-4 h-4" />
                            Share Story
                        </button>
                    )}

                    {/* View Public Story Button - Only for approved stories */}
                    {story.status === 'approved' && (
                        <button
                            onClick={() => navigate(`/success-stories/${story._id}`)}
                            className="flex-1 px-4 sm:px-6 py-2 sm:py-3 border border-primary-600 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900 font-medium text-sm sm:text-base rounded-lg transition-colors"
                        >
                            View Public Story
                        </button>
                    )}
                </div>

                {/* Metadata */}
                <div className="mt-8 sm:mt-10 md:mt-12 pt-4 sm:pt-6 md:pt-8 border-t border-gray-200 dark:border-gray-700 text-xs sm:text-ui-sm text-gray-600 dark:text-gray-400">
                    <p className="mb-1 sm:mb-2">Created: {new Date(story.createdAt).toLocaleDateString()}</p>
                    <p className="mb-1 sm:mb-2">Last updated: {new Date(story.updatedAt).toLocaleDateString()}</p>
                    {story.approvedAt && (
                        <p>Published: {new Date(story.approvedAt).toLocaleDateString()}</p>
                    )}
                </div>
            </div>

            {/* Share Modal */}
            {story && (
                <ShareModal
                    isOpen={shareModalOpen}
                    onClose={() => setShareModalOpen(false)}
                    storyTitle={story.title}
                    storyUrl={`${window.location.origin}/success-stories/${story._id}`}
                />
            )}
        </div>
    )
}

export default MySuccessStoryPage